import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import i18next from 'i18next';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, ENUMS } from 'services/consts';

import { getCountryList } from 'stores/globalAddressSlice';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const HomePageAnnouncementForm = NiceModal.create(({ id, modalName, mode }) => {
	const modal = useModal();

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();

	const isViewMode = useMemo(() => mode === 'view', [mode]);

	const schema = useMemo(
		() =>
			yup.object().shape({
				header: yup.string().nullable().required(),
				subheader: yup.string().nullable(),
				content: yup.string().nullable().required(),
				expiryDate: yup.date().nullable().required(),
				type: yup.string().nullable().required(),
				actionLabel: yup.string().nullable().required(),
				actionType: yup.string().nullable().required(),
				actionUrl: yup
					.string()
					.nullable()
					.when('actionType', {
						is: (val) => val !== 'copy',
						then: (schema) => schema.required()
					}),
				actionContent: yup
					.string()
					.nullable()
					.when('actionType', {
						is: 'copy',
						then: (schema) => schema.required()
					}),
				videoUrl: yup.string().nullable(),
				desktopBGImage: yup
					.object()
					.shape({
						engValue: yup.string().nullable().required(),
						arabicValue: yup.string().nullable(),
						trValue: yup.string().nullable()
					})
					.nullable()
					.required(),
				mobileBGImage: yup
					.object()
					.shape({
						engValue: yup.string().nullable(),
						arabicValue: yup.string().nullable(),
						trValue: yup.string().nullable()
					})
					.nullable()
					.required(),
				showLearnMore: yup.boolean().nullable().required(),
				availableCountries: yup.array().nullable(),
				availablePlans: yup.array().nullable(),
				status: yup.string().nullable().required()
			}),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			header: null,
			subheader: null,
			content: null,
			expiryDate: null,
			type: null,
			actionLabel: null,
			actionUrl: null,
			videoUrl: null,
			desktopBGImage: null,
			mobileBGImage: null,
			showLearnMore: false,
			availableCountries: null,
			availablePlans: null,
			status: null,
			actionType: null,
			actionContent: null
		};

		if (!id) {
			return defaultValues;
		}
		try {
			const { data, status } = await axiosPrivate.post(ADMIN.GET_ANNOUNCEMENT, { id });
			if (status === 200) {
				return {
					...defaultValues,
					...data
				};
			} else {
				AppLogger.error(data);
				showError();
				closeModal();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const isLoading = isFormLoading || isSubmitting;

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = (data) => {
		const missingTranslations = [];
		const requiredFields = ['header', 'content', 'type', 'actionLabel'];

		requiredFields.forEach((field) => {
			if (data[field] && !i18next.exists(data[field])) {
				missingTranslations.push(field);
			}
		});

		if (missingTranslations.length > 0) {
			showError({
				header: 'Missing Translations',
				message: `The following fields are missing translations: ${missingTranslations.join(', ')}`
			});
			return;
		}

		const apiPath = mode === 'edit' ? ADMIN.UPDATE_ANNOUNCEMENT : ADMIN.ADD_ANNOUNCEMENT;
		axiosPrivate
			.post(apiPath, {
				...data,
				id
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.HOME_PAGE_ANNOUNCEMENTS));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					header: 'error',
					message: error.response?.data?.code
				});
			});
	};

	const modalTitle = useMemo(() => {
		switch (mode) {
			case 'create':
				return 'createAnnouncement';
			case 'edit':
				return 'editAnnouncement';
			default:
				return 'announcementDetails';
		}
	}, [mode]);

	useEffect(() => {
		dispatch(getCountryList());
	}, []);
	const countryList = useSelector((state) => state?.globalAddress?.countryList);

	return (
		<Modal fullwidth name={modalName} maxWidth="x-large">
			<ModalTitle>
				<Typography variant="p1">{modalTitle}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="header" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<StringInput name="header" disabled={isViewMode} />
					</FormItem>
					<FormItem label="subheader" isLoading={isLoading} colSpan={{ md: 6 }}>
						<StringInput name="subheader" disabled={isViewMode} />
					</FormItem>
					<FormItem label="content" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<StringInput name="content" disabled={isViewMode} />
					</FormItem>
					<FormItem label="expiryDate" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<DateInput
							name="expiryDate"
							value={watch('expiryDate')}
							setValue={(value) => {
								setValue('expiryDate', value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={isViewMode}
						/>
					</FormItem>
					<FormItem label="availableCountries" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<EnumSelect
							labelPrefix="country."
							placeholder="country"
							data={countryList.map((item) => item?.shortCode) ?? []}
							name="availableCountries"
							value={watch('availableCountries')}
							setValue={(value) => {
								setValue('availableCountries', value || [], {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							multipleSelection
							disabled={isViewMode}
						/>
					</FormItem>
					<FormItem label="availablePlans" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<EnumSelect
							name="availablePlans"
							enumName={ENUMS.CLIENT_PLANS}
							value={watch('availablePlans')}
							setValue={(value) => {
								setValue('availablePlans', value || [], {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							multipleSelection
							disabled={isViewMode}
						/>
					</FormItem>
					<FormItem label="status" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<EnumSelect
							name="status"
							enumName={ENUMS.ACTIVE_INACTIVE}
							disabled={isViewMode}
							value={watch('status')}
							setValue={(value) => {
								setValue('status', value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>
					<FormItem label="type" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<StringInput name="type" disabled={isViewMode} />
					</FormItem>
					<FormItem label="actionLabel" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<StringInput name="actionLabel" disabled={isViewMode} />
					</FormItem>
					<FormItem label="actionType" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<EnumSelect
							name="actionType"
							enumName={ENUMS.ANNOUNCEMENT_ACTION_TYPES}
							disabled={isViewMode}
							value={watch('actionType')}
							setValue={(value) => {
								setValue('actionType', value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>
					{watch('actionType') === 'copy' && (
						<FormItem label="actionContent" isLoading={isLoading} required>
							<StringInput name="actionContent" disabled={isViewMode} />
						</FormItem>
					)}
					{(watch('actionType') === 'navigate' || watch('actionType') === 'navigateInNewTab') && (
						<FormItem label="actionUrl" isLoading={isLoading} required>
							<StringInput name="actionUrl" disabled={isViewMode} />
						</FormItem>
					)}
					<FormItem label="showLearnMore" isLoading={isLoading} required colSpan={{ md: 6 }}>
						<SwitchInput name="showLearnMore" disabled={isViewMode} />
					</FormItem>
					<FormItem label="videoUrl" isLoading={isLoading}>
						<StringInput name="videoUrl" disabled={isViewMode} />
					</FormItem>
					<FormItem label="desktopBGImageEnValue" isLoading={isLoading} required>
						<StringInput name="desktopBGImage.engValue" disabled={isViewMode} />
					</FormItem>
					<FormItem label="desktopBGImageTrValue" isLoading={isLoading}>
						<StringInput name="desktopBGImage.trValue" disabled={isViewMode} />
					</FormItem>
					<FormItem label="desktopBGImageArValue" isLoading={isLoading}>
						<StringInput name="desktopBGImage.arabicValue" disabled={isViewMode} />
					</FormItem>
					<FormItem label="mobileBGImageEnValue" isLoading={isLoading} required>
						<StringInput name="mobileBGImage.engValue" disabled={isViewMode} />
					</FormItem>
					<FormItem label="mobileBGImageTrValue" isLoading={isLoading}>
						<StringInput name="mobileBGImage.trValue" disabled={isViewMode} />
					</FormItem>
					<FormItem label="mobileBGImageArValue" isLoading={isLoading}>
						<StringInput name="mobileBGImage.arabicValue" disabled={isViewMode} />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={isViewMode ? 'close' : 'cancel'} variant="outline" onClick={handleCloseModal} />
				{!isViewMode && (
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : 'button.add'}
						disabled={!isDirty}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default HomePageAnnouncementForm;
